import { IBalusterGeometry, IRailingGeometry, ISupportPosts, IVec3 } from '@stair-craft/common/util';

/** How much a height difference counts against a plan distance when picking the rail segment. */
const HEIGHT_HINT_WEIGHT: number = 0.05;

/** An upright placed along its rail: the distance along the rail in plan and its own width. */
interface IRailStation {
    readonly along: number;
    readonly width: number;
}

/** A clear gap between two neighbouring uprights, by the distance along the rail. */
export interface IRailGap {
    readonly from: number;
    readonly to: number;
    readonly clear: number;
}

/**
 * The distance along the rail in plan to the point nearest an upright. Ties between levels (a
 * spiral past a full turn) are broken by the height of the upright's top.
 */
function stationOf(points: readonly IVec3[], at: IVec3, topY: number): number {
    let best: { score: number; along: number } = { score: Infinity, along: 0 };
    let walked: number = 0;
    for (let i: number = 0; i < points.length - 1; i++) {
        const a: IVec3 = points[i];
        const b: IVec3 = points[i + 1];
        const dx: number = b.x - a.x;
        const dz: number = b.z - a.z;
        const length: number = Math.hypot(dx, dz);
        if (length > 1e-3) {
            const t: number = Math.max(0, Math.min(1, ((at.x - a.x) * dx + (at.z - a.z) * dz) / (length * length)));
            const y: number = a.y + t * (b.y - a.y);
            const score: number = Math.hypot(a.x + t * dx - at.x, a.z + t * dz - at.z) + HEIGHT_HINT_WEIGHT * Math.abs(y - topY);
            if (score < best.score) {
                best = { score, along: walked + t * length };
            }
        }
        walked += length;
    }
    return best.along;
}

/** The rail point at a distance along it in plan. */
export function railPointAt(points: readonly IVec3[], along: number): IVec3 {
    let walked: number = 0;
    for (let i: number = 0; i < points.length - 1; i++) {
        const a: IVec3 = points[i];
        const b: IVec3 = points[i + 1];
        const length: number = Math.hypot(b.x - a.x, b.z - a.z);
        if (length > 1e-3 && along <= walked + length) {
            const t: number = Math.max(0, (along - walked) / length);
            return { x: a.x + t * (b.x - a.x), y: a.y + t * (b.y - a.y), z: a.z + t * (b.z - a.z) };
        }
        walked += length;
    }
    return { ...points[points.length - 1] };
}

/** The plan distance from a point to the nearest point of a rail. */
function offRail(points: readonly IVec3[], at: IVec3): number {
    let best: number = Infinity;
    for (let i: number = 0; i < points.length - 1; i++) {
        const a: IVec3 = points[i];
        const b: IVec3 = points[i + 1];
        const dx: number = b.x - a.x;
        const dz: number = b.z - a.z;
        const lengthSq: number = dx * dx + dz * dz;
        const t: number = lengthSq > 1e-6 ? Math.max(0, Math.min(1, ((at.x - a.x) * dx + (at.z - a.z) * dz) / lengthSq)) : 0;
        best = Math.min(best, Math.hypot(a.x + t * dx - at.x, a.z + t * dz - at.z));
    }
    return best;
}

/**
 * The clear gaps between neighbouring posts and balusters of one railing, along its rail. A turn's
 * support post standing on the rail line (SC-CALC-093) closes the rail there like its own post.
 */
export function railGaps(railing: IRailingGeometry, supports?: ISupportPosts): IRailGap[] {
    const points: IVec3[] = railing.handrail.points;
    const station: (upright: IBalusterGeometry, width: number) => IRailStation = (
        upright: IBalusterGeometry,
        width: number
    ): IRailStation => ({
        width,
        along: stationOf(points, upright.position, upright.position.y + upright.height),
    });
    const onRail: IBalusterGeometry[] = (supports?.posts ?? []).filter(
        (post: IBalusterGeometry) => offRail(points, post.position) < (supports?.width ?? 0) / 2
    );
    const stations: IRailStation[] = [
        ...railing.posts.map((post: IBalusterGeometry) => station(post, railing.postWidth)),
        ...onRail.map((post: IBalusterGeometry) => station(post, supports?.width ?? 0)),
        ...railing.balusters.map((baluster: IBalusterGeometry) => station(baluster, railing.balusterWidth)),
    ].sort((left: IRailStation, right: IRailStation) => left.along - right.along);
    const gaps: IRailGap[] = [];
    for (let i: number = 1; i < stations.length; i++) {
        const from: number = stations[i - 1].along + stations[i - 1].width / 2;
        const to: number = stations[i].along - stations[i].width / 2;
        gaps.push({ from, to, clear: to - from });
    }
    return gaps;
}

/** The widest clear gap of a railing; 0 when it has fewer than two uprights. */
export function widestRailGap(railing: IRailingGeometry): number {
    return Math.max(0, ...railGaps(railing).map((gap: IRailGap) => gap.clear));
}

/**
 * Balusters that close the gaps wider than `limit`: as few as bring every part of such a gap
 * down to `limit`, spread evenly over it, each standing on `base` up to the rail. The caller
 * seats them.
 */
export function gapFillers(
    railing: IRailingGeometry,
    limit: number,
    base: (point: IVec3) => number,
    supports?: ISupportPosts
): IBalusterGeometry[] {
    const width: number = railing.balusterWidth;
    const fillers: IBalusterGeometry[] = [];
    for (const gap of railGaps(railing, supports)) {
        if (gap.clear <= limit + 0.5) {
            continue;
        }
        const count: number = Math.ceil((gap.clear - limit) / (limit + width));
        const pitch: number = (gap.clear - count * width) / (count + 1);
        for (let i: number = 1; i <= count; i++) {
            const along: number = gap.from + i * pitch + (i - 0.5) * width;
            const rail: IVec3 = railPointAt(railing.handrail.points, along);
            const y: number = base(rail);
            fillers.push({ position: { y, x: rail.x, z: rail.z }, height: Math.max(0, rail.y - y), rotation: 0 });
        }
    }
    return fillers;
}
